import React, { useCallback, useContext, useMemo, useState } from 'react'

import { ConnectionCard } from '@/components/connectionCard/ConnectionCard'

import {
  connectionsSearchInput,
  connectionsSearchInputContainer,
  connectionsSearchInputIcon,
} from '@/components/pages/profile_v2/connections/Connections.css'
import { cleanUsername } from '@/components/social-networks/misc/utils'
import { LoaderSpinner } from '@/components/ui/LoaderSpinner.component'
import { IUser } from '@/interfaces/user'
import { getUsersByIds } from '@/services/user'
import { useProfilePage } from '@/shared/hooks/profilePage'
import { useQuery } from '@/shared/hooks/useQuery'
import Fuse from 'fuse.js'
import { SearchIcon } from '../../../../public/assets/v2/images/icons'
import { UserContext } from 'context'
import { RequestConnectionButton } from 'containers/profile/components/buttons/RequestConnectionButton'

export const AllConnections: React.FC = () => {
  const { user } = useContext(UserContext)
  const { profile, ownProfile } = useProfilePage()
  const { connections } = profile

  const {
    data: userConnections,
    isCompleted,
    isLoading,
    setData,
  } = useQuery<IUser[]>({
    queryMethod: getUsersByIds,
    queryArguments: connections,
    useFetchEffect: connections?.length > 0,
  })

  const [searchTerm, setSearchTerm] = useState('')
  const cleanedSearchTerm = searchTerm.replace(/[^\w\s]/gi, '')

  const parsedConnections = useMemo(() => {
    return userConnections.filter((connection) => {
      return connections.includes(connection?.id)
    })
  }, [userConnections, connections])

  const searchableConnections = useMemo(() => {
    return new Fuse(parsedConnections, {
      shouldSort: true,
      keys: ['firstName', 'lastName', 'username', 'email'],
    })
  }, [parsedConnections])

  const filteredConnections = useMemo(() => {
    if (!cleanedSearchTerm) return parsedConnections
    return searchableConnections
      .search(cleanedSearchTerm)
      .map((result) => result.item)
  }, [cleanedSearchTerm, parsedConnections, searchableConnections])

  const onRemoveSuccess = useCallback(
    (connectionId: string) => {
      setData(
        parsedConnections.filter((connection) => connection.id !== connectionId)
      )
    },
    [parsedConnections, setData]
  )

  if (!filteredConnections) {
    const userLabel = ownProfile ? "you don't" : "this user doesn't"

    return (
      <div className="flex flex-column items-center justigy-center full-w mt-lg">
        <h4 className="cl-primary fs-18 fw-md">
          Looks like {userLabel} have any connections yet
        </h4>
        😄
      </div>
    )
  }

  if (isLoading && !isCompleted) {
    return (
      <span className="cl-text-secondary fw-md fs-18">
        Connections{' '}
        <span className="cl-pink-primary fs-20">
          <LoaderSpinner />
        </span>
      </span>
    )
  }

  return (
    <div className="flex flex-column gap-26">
      <div>
        <span className="cl-text-secondary fw-md fs-18">
          Connections{' '}
          <span className="cl-pink-primary fs-20">
            {filteredConnections?.length.toLocaleString() || 0}
          </span>
        </span>
      </div>
      <div>
        <SearchInput value={cleanedSearchTerm} onChange={setSearchTerm} />
      </div>
      <div className="flex flex-column gap-14">
        {filteredConnections.map((connection, index) => {
          const canSendMessage = user.connections?.includes(connection.id)
          const canAddFriend = !user.connections?.includes(connection.id)

          return (
            <ConnectionCard
              connection={connection}
              key={`${connection.id}-${index}`}
            >
              {canSendMessage && <ConnectionCard.SendMessageButton />}
              {canAddFriend && user.id !== connection.id && (
                <RequestConnectionButton profile={connection} />
              )}

              <ConnectionCard.ViewProfileButton />
              <ConnectionCard.ViewMoreButton onSuccess={onRemoveSuccess} />
            </ConnectionCard>
          )
        })}
      </div>
    </div>
  )
}

const SearchInput = ({ value, onChange }) => {
  const { ownProfile } = useProfilePage()
  return (
    <div className={connectionsSearchInputContainer}>
      <span className={connectionsSearchInputIcon}>
        <SearchIcon />
      </span>
      <input
        className={connectionsSearchInput}
        autoFocus
        placeholder={`Search ${ownProfile ? 'your' : 'through'} connections...`}
        value={value}
        onChange={(e) => onChange(cleanUsername(e.target.value))}
      />
    </div>
  )
}
